import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  TrendingUp, TrendingDown, Minus, BarChart3, RefreshCw, Loader2,
  ArrowUp, ArrowDown, Search, Globe, ExternalLink, AlertTriangle, Settings, Check, X
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Performance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("clicks_now");

  const [forceRefresh, setForceRefresh] = useState(false);
  const [showGaManager, setShowGaManager] = useState(false);
  const [unmatchedSites, setUnmatchedSites] = useState([]);
  const [gaInputs, setGaInputs] = useState({});
  const [savingGa, setSavingGa] = useState(null);

  const fetchData = async (force = false) => {
    setForceRefresh(force);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/performance/dashboard${force ? '?force=true' : ''}`);
      setData(res.data);
    } catch(e) {}
    setLoading(false);
  };

  const fetchUnmatched = async () => {
    try {
      const res = await axios.get(`${API}/analytics/unmatched`);
      setUnmatchedSites(res.data.unmatched || []);
    } catch {}
  };

  const saveGaProperty = async (siteId, gaId) => {
    setSavingGa(siteId);
    try {
      await axios.put(`${API}/sites/${siteId}/ga-property`, { ga_property_id: gaId });
      setUnmatchedSites(prev => prev.filter(s => s.id !== siteId));
      setGaInputs(prev => { const n = {...prev}; delete n[siteId]; return n; });
    } catch {}
    setSavingGa(null);
  };

  const syncGaProperties = async () => {
    try {
      const res = await axios.post(`${API}/analytics/sync-properties`);
      const msg = res.data;
      fetchUnmatched();
      fetchData(false);
    } catch {}
  };

  useEffect(() => { fetchData(false); fetchUnmatched(); }, []);

  const TrendIcon = ({trend}) => {
    if (trend === "growing") return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (trend === "declining") return <ArrowDown className="w-4 h-4 text-red-500" />;
    if (trend === "no_data") return <Minus className="w-4 h-4 text-gray-300" />;
    return <Minus className="w-4 h-4 text-amber-500" />;
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (data?.error) return <Card><CardContent className="p-8 text-center text-red-500">{data.error}</CardContent></Card>;

  const filtered = (data?.sites || [])
    .filter(s => !search || s.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sortBy === "click_pct" ? b.click_pct - a.click_pct : b[sortBy] - a[sortBy]);

  const s = data?.stats || {};

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="w-8 h-8 text-blue-500" /> Efektywnosc stron</h1>
          <p className="text-muted-foreground text-sm mt-1">Search Console + Google Analytics | {data?.period}</p>
        </div>
      <ModuleDescription title="Jak czytac dane efektywnosci?">
        <p><strong>Dashboard Efektywnosci</strong> laczy dane z Google Search Console (kliki, wyswietlenia, pozycja) i Google Analytics (uzytkownicy, sesje).</p>
        <p><strong>Kliki</strong> = ile razy ktos kliknal Twoja strone w wynikach Google w ostatnich 14 dniach.</p>
        <p><strong>Zmiana / %</strong> = porownanie z poprzednimi 14 dniami. Zielony = wzrost, czerwony = spadek.</p>
        <p><strong>GA Users / Sesje</strong> = dane z Google Analytics (odwiedzajacy). Jesli "-" = brak GA Property ID. Kliknij "GA" aby przypisac recznie.</p>
        <p><strong>"Odswierz (live)"</strong> = pobiera dane na zywo z Google API (moze trwac 1-2 min). Normalnie dane sa cache-owane i odswiezane co 24h przez agenta.</p>
      </ModuleDescription>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { setShowGaManager(!showGaManager); if (!showGaManager) fetchUnmatched(); }} className="gap-1.5" data-testid="ga-manager-btn">
            <Settings className="w-3.5 h-3.5" /> GA ({unmatchedSites.length} brak)
          </Button>
          <Button size="sm" onClick={() => fetchData(true)} disabled={loading} className="gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> {loading ? "Ladowanie..." : "Odswierz (live)"}</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{s.total_clicks_now}</p>
          <p className="text-xs text-muted-foreground">Kliki (14 dni)</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className={`text-2xl font-bold ${s.total_change >= 0 ? "text-green-600" : "text-red-500"}`}>
            {s.total_change >= 0 ? "+" : ""}{s.total_change}
          </p>
          <p className="text-xs text-muted-foreground">Zmiana vs poprz. 14d</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className={`text-2xl font-bold ${s.total_change_pct >= 0 ? "text-green-600" : "text-red-500"}`}>
            {s.total_change_pct >= 0 ? "+" : ""}{s.total_change_pct}%
          </p>
          <p className="text-xs text-muted-foreground">Wzrost %</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{s.growing}</p>
          <p className="text-xs text-muted-foreground">Rosna</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-red-500">{s.declining}</p>
          <p className="text-xs text-muted-foreground">Spadaja</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-400">{s.no_data}</p>
          <p className="text-xs text-muted-foreground">Brak danych</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{(data?.sites || []).reduce((sum, s) => sum + (s.ga_users || 0), 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">GA Users (7d)</p>
        </CardContent></Card>
      </div>

      {/* GA Property Manager */}
      {showGaManager && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-1.5">
                <Settings className="w-4 h-4 text-amber-600" /> Przypisz GA Property ID ({unmatchedSites.length} stron bez GA)
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={syncGaProperties} className="gap-1 text-xs h-7">
                  <RefreshCw className="w-3 h-3" /> Auto-sync
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowGaManager(false)} className="h-7 w-7 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">Wklej GA4 Property ID (Identyfikator uslugi) z Google Analytics. Znajdziesz go w Admin &gt; Ustawienia uslugi &gt; Szczegoly uslugi.</p>
            <ScrollArea className="max-h-[250px]">
              <div className="space-y-1.5">
                {unmatchedSites.map(site => (
                  <div key={site.id} className="flex items-center gap-2 bg-muted/30 rounded px-3 py-2" data-testid={`ga-assign-${site.id}`}>
                    <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-xs font-medium w-40 truncate">{site.name}</span>
                    <Input
                      placeholder="GA Property ID (np. 362764741)"
                      value={gaInputs[site.id] || ""}
                      onChange={e => setGaInputs(prev => ({...prev, [site.id]: e.target.value}))}
                      className="h-7 text-xs flex-1"
                    />
                    <Button
                      size="sm"
                      className="h-7 text-xs gap-0.5 bg-amber-600 hover:bg-amber-700"
                      disabled={!gaInputs[site.id] || savingGa === site.id}
                      onClick={() => saveGaProperty(site.id, gaInputs[site.id])}
                    >
                      {savingGa === site.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Zapisz
                    </Button>
                  </div>
                ))}
                {unmatchedSites.length === 0 && (
                  <div className="text-center py-4 text-xs text-green-600">Wszystkie strony maja przypisane GA Property ID!</div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Ranking stron ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Szukaj..." value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-xs pl-8 w-48" />
              </div>
              <select className="h-8 text-xs border rounded px-2" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="clicks_now">Kliki (teraz)</option>
                <option value="click_change">Zmiana klikow</option>
                <option value="click_pct">Wzrost %</option>
                <option value="impressions_now">Wyswietlenia</option>
                <option value="posts_count">Artykuly</option>
                <option value="ga_users">GA Users</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white dark:bg-gray-950">
                <tr className="border-b text-left">
                  <th className="py-2 px-3 font-medium w-8">#</th>
                  <th className="py-2 font-medium">Strona</th>
                  <th className="py-2 font-medium text-center">Trend</th>
                  <th className="py-2 font-medium text-right">Kliki</th>
                  <th className="py-2 font-medium text-right">Zmiana</th>
                  <th className="py-2 font-medium text-right">%</th>
                  <th className="py-2 font-medium text-right">Wyswietlenia</th>
                  <th className="py-2 font-medium text-right">Pozycja</th>
                  <th className="py-2 font-medium text-right">Artykuly</th>
                  <th className="py-2 font-medium text-right">GA Users</th>
                  <th className="py-2 font-medium text-right">GA Sesje</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((site, i) => (
                  <tr key={site.site_id} className={`border-b hover:bg-muted/30 ${site.trend === "growing" ? "bg-green-50/30" : site.trend === "declining" ? "bg-red-50/20" : ""}`}>
                    <td className="py-2 px-3 text-muted-foreground">{i+1}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{site.name}</span>
                        {site.google_news && <Badge className="text-[7px] bg-green-100 text-green-700 px-1">GN</Badge>}
                      </div>
                      <span className="text-[10px] text-muted-foreground">{site.category}</span>
                    </td>
                    <td className="py-2 text-center"><TrendIcon trend={site.trend} /></td>
                    <td className="py-2 text-right font-bold">{site.clicks_now}</td>
                    <td className={`py-2 text-right font-medium ${site.click_change > 0 ? "text-green-600" : site.click_change < 0 ? "text-red-500" : "text-gray-400"}`}>
                      {site.click_change > 0 ? "+" : ""}{site.click_change}
                    </td>
                    <td className={`py-2 text-right ${site.click_pct > 0 ? "text-green-600" : site.click_pct < 0 ? "text-red-500" : "text-gray-400"}`}>
                      {site.click_pct > 0 ? "+" : ""}{site.click_pct}%
                    </td>
                    <td className="py-2 text-right">{site.impressions_now}</td>
                    <td className="py-2 text-right">{site.position > 0 ? site.position : "-"}</td>
                    <td className="py-2 text-right text-muted-foreground">{site.posts_count}</td>
                    <td className="py-2 text-right font-medium text-purple-600">{site.ga_users || "-"}</td>
                    <td className="py-2 text-right">{site.ga_sessions || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
